import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ViewChange } from '@/lib/saved-view';

interface Props {
  open: boolean;
  /** What is being saved: "report" or "dashboard". */
  noun: string;
  /** What differs, so the question names it — not "filters" when only the period does. */
  changed: ViewChange;
  /** The saved view, described — "Last month · Role". */
  saved: string;
  /** The view on screen, described the same way. */
  current: string;
  onSaveMine: () => void;
  onKeepSaved: () => void;
  /** Back to editing; nothing is saved. */
  onCancel: () => void;
  /** A save is in flight: every button waits on it. */
  saving?: boolean;
}

/**
 * Asked when an owner SAVES while the filters or period on screen differ from
 * the ones the report or dashboard is saved with.
 *
 * The bar is the viewer's — changing it is never an edit, the owner's
 * included — so whether it becomes everyone's is a question, asked at the one
 * moment it matters, rather than a separate button nobody knew to press.
 */
export function SaveFiltersDialog({
  open,
  noun,
  changed,
  saved,
  current,
  onSaveMine,
  onKeepSaved,
  onCancel,
  saving = false,
}: Props): React.ReactElement {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // ESC / overlay click is Cancel: nothing saved, back to the edit.
        if (!next && !saving) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save your {changed} too?</DialogTitle>
          <DialogDescription>
            Your {changed} {changed === 'period' ? 'is' : 'are'} different from what this {noun} is
            saved with. Everyone it’s shared with opens it on the saved {changed}.
          </DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Saved</dt>
          <dd data-testid="save-filters-saved">{saved}</dd>
          <dt className="text-muted-foreground">Yours</dt>
          <dd data-testid="save-filters-current">{current}</dd>
        </dl>
        <DialogFooter>
          <Button variant="ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" disabled={saving} onClick={onKeepSaved}>
            Keep saved {changed}
          </Button>
          <Button disabled={saving} onClick={onSaveMine}>
            {saving ? 'Saving…' : `Save my ${changed}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
